import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Injector,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { BaseModalComponent } from '../../../../../core/modals/components/base-modal/base-modal.component';
import { emailPatternValidator } from '../../../../../shared/form-validators/email-pattern-validator';
import { Actions, ofType } from '@ngrx/effects';
import { UserApiActions } from '../../../../../core/users/actions/user-api.actions';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AsyncStateSelectors } from 'projects/nutrition/src/app/core/async-state/selectors/async-state.selectors';
import { UsersSelectors } from 'projects/webk/src/app/core/users/selectors/users-selectors';

type Step = 'enterEmail' | 'confirmInvite' | 'composeEmail';

@Component({
  selector: 'app-invite-monitored-user',
  templateUrl: './invite-monitored-user.component.html',
  styleUrls: ['./invite-monitored-user.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InviteMonitoredUserComponent
  extends BaseModalComponent<{}>
  implements OnInit, OnDestroy
{
  step: Step = 'enterEmail';

  errorState = this.store.select(
    AsyncStateSelectors.makeGetAsyncErrorState(
      UserApiActions.UserApiActionTypes.SubmitMonitoredUserInvitation
    )
  );

  emailAddress = new FormControl('', [Validators.required, emailPatternValidator]);

  composeForm: FormGroup = this.fb.group({
    to: this.fb.control('', [Validators.required, emailPatternValidator]),
    subject: this.fb.control('', [Validators.required, Validators.maxLength(200)]),
    body: this.fb.control('', [Validators.required]),
  });

  asyncProcessKeys = [
    UserApiActions.submitMonitoredUserInvitation,
    UserApiActions.submitMonitoredUserInvitationEmail,
  ].map((t: any) => t.type ?? t);

  private destroy$ = new Subject<void>();

  constructor(
    injector: Injector,
    private fb: FormBuilder,
    private actions$: Actions,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.store
      .select(UsersSelectors.selectPendingInvitationEmail)
      .pipe(takeUntil(this.destroy$))
      .subscribe((email) => {
        if (email) {
          this.composeForm.patchValue({ to: email });
          this.step = 'confirmInvite';
          this.cdr.markForCheck();
        }
      });

    this.actions$
      .pipe(
        ofType(UserApiActions.submitMonitoredUserInvitationEmailSuccess),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.close());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  handleCheckEmail(): void {
    if (this.emailAddress.invalid) {
      this.emailAddress.markAsTouched();
      return;
    }

    this.store.dispatch(
      UserApiActions.submitMonitoredUserInvitation({
        email: this.emailAddress.value!,
      })
    );
  }

  handleDeclineInvite(): void {
    this.close();
  }

  handleProceedToCompose(): void {
    const to = (this.emailAddress.value || '').trim();
    const subject =
      this.translate.instant('modal.invite_monitored_user.default_subject')
    const body =
      this.translate.instant('modal.invite_monitored_user.default_body')
    this.composeForm.patchValue({ to, subject, body });
    this.step = 'composeEmail';
    this.cdr.markForCheck();
  }

  handleSendInvitation(): void {
    if (this.composeForm.invalid) {
      this.composeForm.markAllAsTouched();
      return;
    }

    const { to, subject, body } = this.composeForm.value;

    this.store.dispatch(
      UserApiActions.submitMonitoredUserInvitationEmail({
        to,
        subject,
        body,
      })
    );
  }
}
